<template>
  <div class="page-container">
    <div class="content-banner">
      <img
        src="@/assets/images/umbrella-campaigns-banner.png"
        alt="Umbrella Campaigns Banner"
      />
    </div>
    <div class="title-header">
      <div class="title">Sepette İndirim Kampanya Teklifleri</div>
    </div>
    <!-- Tabs and Filter Container -->
    <div class="content-wrapper">
      <div class="tabs-container">
        <button
          :class="[
            'tab-button',
            { active: activeTab === CAMPAIGN_TABS.available },
          ]"
          @click="handleTabClick(CAMPAIGN_TABS.available)"
        >
          <span class="label">Katılabileceğiniz Kampanyalar</span>
          <span class="count">({{ availableCampaignsCount }})</span>
        </button>
        <button
          :class="[
            'tab-button',
            { active: activeTab === CAMPAIGN_TABS.joined },
          ]"
          @click="handleTabClick(CAMPAIGN_TABS.joined)"
        >
          <span class="label">Katıldığınız Kampanyalar</span>
          <span class="count">({{ joinedCampaignsCount }})</span>
        </button>
      </div>
      <CampaignFilters
        :ref="filtersRef"
        :filters="filters"
        :categories="categories"
        :brands="brands"
        @update:filters="handleFiltersUpdate"
        @apply-filters="applyFilters"
        @clear-filters="clearFilters"
      />
    </div>
    <CampaignTable
      :isLoading="isLoading"
      :campaigns="campaigns"
      :current-page="currentPage"
      :total-pages="totalPages"
      :page-size="pageSize"
      :sort-by="sortBy"
      :active-tab="activeTab"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
      @sort-change="handleSortChange"
      @go-to-detail="goToCampaignDetail"
      @join-campaign="joinCampaign"
    />
    <div v-if="showJoinCampaignModal" class="modal-wrapper">
      <div class="modal-content">
        <button class="modal-close" @click="closeModal">
          <Icons name="Close" color="#1C1C1E" size="sm" />
        </button>
        <Icons name="InfoBlueCircle" color="#4FA4E5" size="lg" />
        <p class="modal-description">
          Kampanya katılım durumunuz onaylı olmadığı için katılamazsınız. Mağaza
          Destek bölümünden kampanya katılım onayı verebilirsiniz.
        </p>
        <AppButton primary md title="Tamam" @buttonClick="closeModal" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Icons, AppButton } from '@n11/so-ui-components';
import {
  sendButtonClickEvent,
  sendPageViewEvent,
  sendFilterShowResultEvent,
  sendSortApplyEvent,
  sendJoinCampaignEvent,
} from '@/utils/ga4/helper';
import { ToastMessageTypes } from '@/utils/enum/toastMessageTypes';
import { CAMPAIGN_TABS } from '@/utils/enum/umbrellaCampaign';
import {
  getCampaigns,
  getSellerInfo,
  getPromotionalNoticeStatus,
} from './services/umbrellaCampaignService';
import CampaignFilters from './partials/CampaignFilters';
import CampaignTable from './partials/CampaignTable';

const route = useRoute();
const router = useRouter();
const store = useStore();

// Route query'den tab bilgisini al
const activeTab = ref(
  route.query.tab === CAMPAIGN_TABS.joined
    ? CAMPAIGN_TABS.joined
    : CAMPAIGN_TABS.available,
);

const newlyJoinedCampaigns = ref([]);
const filters = ref({
  campaign: '',
  category: [],
  brand: [],
});
const categories = ref([]);
const brands = ref([]);
const sortBy = ref('');
const currentPage = ref(0);
const totalPages = ref(0);
const pageSize = ref(10);
const isLoading = ref(false);

const campaigns = ref([]);
const availableCampaignsCount = ref(0);
const joinedCampaignsCount = ref(0);
const showJoinCampaignModal = ref(false);
const hasProcessedNewlyJoined = ref(false);

const filtersRef = ref(null);

// Yeni eklenen kampanyaları listeye ekle
const addNewlyJoinedCampaign = async () => {
  if (
    !newlyJoinedCampaigns.value ||
    newlyJoinedCampaigns.value.length === 0 ||
    activeTab.value !== CAMPAIGN_TABS.joined ||
    hasProcessedNewlyJoined.value
  ) {
    return;
  }

  try {
    const joinedCampaignsById = new Map(
      campaigns.value.map((campaign) => [
        String(campaign.id ?? campaign.campaignId ?? ''),
        campaign,
      ]),
    );

    const normalized = newlyJoinedCampaigns.value
      .map((campaign) => {
        const normalizedId = String(campaign.id ?? campaign.campaignId ?? '');
        if (!normalizedId) return null;

        const apiCampaign = joinedCampaignsById.get(normalizedId);
        return {
          ...(apiCampaign || campaign),
          isNewlyJoined: true,
        };
      })
      .filter(Boolean);

    const uniqueCampaigns = new Map();

    normalized.forEach((campaign) => {
      const normalizedId = String(campaign.id ?? campaign.campaignId ?? '');
      if (!normalizedId) return;
      uniqueCampaigns.set(normalizedId, campaign);
    });

    campaigns.value.forEach((campaign) => {
      const normalizedId = String(campaign.id ?? campaign.campaignId ?? '');
      if (!normalizedId || uniqueCampaigns.has(normalizedId)) return;
      uniqueCampaigns.set(normalizedId, campaign);
    });

    campaigns.value = Array.from(uniqueCampaigns.values());

    // Toaster mesajı göster
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.SUCCESS,
      description: 'Kampanyaya katıldınız.',
    });

    // SessionStorage'dan temizle ve flag'i set et
    newlyJoinedCampaigns.value = [];
    hasProcessedNewlyJoined.value = true;
    sessionStorage.removeItem('newlyJoinedCampaign');
  } catch (error) {
    console.error('Error adding newly joined campaign:', error);
  }
};

const fetchCampaigns = async () => {
  isLoading.value = true;
  try {
    const params = {
      page: currentPage.value,
      size: pageSize.value,
      tab:
        activeTab.value === CAMPAIGN_TABS.available
          ? CAMPAIGN_TABS.available
          : CAMPAIGN_TABS.joined,
      ...(filters.value.campaign && { campaignName: filters.value.campaign }),
      ...(filters.value.category &&
        filters.value.category.length > 0 && {
          categoryIds: filters.value.category.map((item) => item.id),
        }),
      ...(filters.value.brand &&
        filters.value.brand.length > 0 && {
          brands: filters.value.brand.map((item) => item.name || item.id),
        }),
      ...(sortBy.value && { sortType: sortBy.value }),
    };

    const result = await getCampaigns(params);

    campaigns.value = result.campaigns || [];
    totalPages.value = result.totalPages;

    sortBy.value = result.sortType || null;

    availableCampaignsCount.value = result.joinableCount || 0;
    joinedCampaignsCount.value = result.joinedCount || 0;
  } catch (error) {
    campaigns.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleFiltersUpdate = (newFilters) => {
  filters.value = { ...newFilters };
};

const buildFilterAnalyticsPayload = () => {
  const groups = [];

  if (filters.value.campaign && filters.value.campaign.trim()) {
    groups.push({
      name: 'Kampanya',
      values: [filters.value.campaign.trim()],
    });
  }

  if (filters.value.category && filters.value.category.length > 0) {
    const categoryNames = filters.value.category
      .map((item) => item.name || item.label || '')
      .filter(Boolean);
    if (categoryNames.length > 0) {
      groups.push({
        name: 'Kategori',
        values: categoryNames,
      });
    }
  }

  if (filters.value.brand && filters.value.brand.length > 0) {
    const brandNames = filters.value.brand
      .map((item) => item.name || item.label || '')
      .filter(Boolean);
    if (brandNames.length > 0) {
      groups.push({
        name: 'Marka',
        values: brandNames,
      });
    }
  }

  const filterCount = groups.length;

  const selectedFilters = groups
    .map((g) => `${g.name}:${g.values.join('|')}`)
    .join(', ');

  const filtersDetail = groups.map((g) => ({
    filter_name: g.name,
    selected_values: g.values,
    selected_count: g.values.length,
  }));

  return {
    filterCount,
    selectedFilters,
    filters: filtersDetail,
  };
};

const applyFilters = () => {
  currentPage.value = 0;

  const {
    filterCount,
    selectedFilters,
    filters: filtersDetail,
  } = buildFilterAnalyticsPayload();

  if (filterCount > 0) {
    sendFilterShowResultEvent({
      page_type: 'Cart Discount Campaigns',
      filter_count: filterCount,
      selected_filters: selectedFilters,
      filters: filtersDetail,
    });
  }

  fetchCampaigns();
};

const clearFilters = () => {
  filters.value = {
    campaign: '',
    category: [],
    brand: [],
  };
  currentPage.value = 0;
  fetchCampaigns();
};

const handlePageChange = (pageIndex) => {
  currentPage.value = pageIndex;
  fetchCampaigns();
};

const handlePageSizeChange = (newPageSize) => {
  pageSize.value = Number(newPageSize);
  currentPage.value = 0;
  fetchCampaigns();
};

const handleSortChange = (selectedSort) => {
  sortBy.value = selectedSort;
  currentPage.value = 0;

  const SORT_LABELS = {
    COMMISSION_DISCOUNT_DESC: 'Komisyona Göre',
    START_DATE_DESC: 'Başlangıç Tarihi',
    END_DATE_DESC: 'Son Katılım Tarihi',
    CREATE_DATE_DESC: 'Oluşturulma Tarihi',
  };

  const sortingLabel = SORT_LABELS[selectedSort] || '';

  if (sortingLabel) {
    sendSortApplyEvent({
      sorting_options: sortingLabel,
    });
  }

  fetchCampaigns();
};

const getTabPageName = (tabKey) =>
  tabKey === CAMPAIGN_TABS.available
    ? 'Katılabileceğiniz Kampanyalar'
    : 'Katıldığınız Kampanyalar';

const fireTabEvents = (tabKey) => {
  const pageName = getTabPageName(tabKey);

  sendButtonClickEvent({
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    button_name: pageName,
    button_type: 'Tab',
    page_type: 'Cart Discount Campaigns',
  });

  sendPageViewEvent({
    page_name: pageName,
    page_type: 'Cart Discount Campaigns',
  });
};

const handleTabClick = (tabKey) => {
  if (activeTab.value === tabKey) return;

  activeTab.value = tabKey;
  fireTabEvents(tabKey);
};

const goToCampaignDetail = (campaignId) => {
  if (!campaignId) return;
  sendButtonClickEvent({
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    button_name: 'Kampanya Detayına Git',
    button_type: 'Campaign Detail',
    page_type: 'Cart Discount Campaigns',
  });
  // Aktif taba göre detail sayfasında gösterilecek modu belirle
  const mode = activeTab.value === CAMPAIGN_TABS.joined ? 'manage' : 'detail';
  sessionStorage.setItem('campaignDetailMode', mode);
  sessionStorage.setItem('campaignDetailId', campaignId.toString());
  router.push({
    name: 'UmbrellaCampaignDetail',
  });
};

const joinCampaign = async (campaignId) => {
  if (!campaignId) return;
  const manageCampaign = campaigns.value.find((c) => c.id === campaignId);

  // Eğer joined tab'ındaysa, kampanyayı yönet sayfasına yönlendir
  if (activeTab.value === CAMPAIGN_TABS.joined) {
    sendButtonClickEvent({
      page_path: typeof window !== 'undefined' ? window.location.pathname : '',
      button_name: 'Kampanyayı Yönet',
      button_type: 'Join Campaign',
      page_type: 'Cart Discount Campaigns',
    });

    sendButtonClickEvent({
      page_path: route.path,
      button_name: 'Kampanyayı Yönet',
      button_type: 'Edit Campaign',
      campaign_id: campaignId,
      campaign_name: manageCampaign?.name || '',
      page_type: 'Campaign Detail',
    });

    router.push({
      name: 'ManageCampaign',
      params: { campaignId: campaignId.toString() },
    });
    return;
  }

  try {
    const promotionalNoticeStatus = await getPromotionalNoticeStatus();

    // Eğer false ise modal'ı göster
    if (!promotionalNoticeStatus) {
      showJoinCampaignModal.value = true;
      return;
    }

    // Eğer true ise, kampanyaya katıl sayfasına yönlendir
    const campaign = campaigns.value.find((c) => c.id === campaignId);

    // campaignName'i sessionStorage'a kaydet
    if (campaign?.name) {
      sessionStorage.setItem('campaignName', campaign.name);
    }

    sendButtonClickEvent({
      page_path: typeof window !== 'undefined' ? window.location.pathname : '',
      button_name: 'Kampanyaya Katıl',
      button_type: 'Join Campaign',
      page_type: 'Cart Discount Campaigns',
    });

    sendJoinCampaignEvent({
      campaign_id: campaignId,
      campaign_name: campaign?.name || '',
      page_type: 'Cart Discount Campaigns',
      item_id: campaign?.id || '',
      value: campaign?.value || '',
      price: campaign?.price || '',
      commission_rate: campaign?.commission_rate || '',
    });

    router.push({
      name: 'JoinCampaign',
      params: { campaignId: campaignId.toString() },
    });
  } catch (error) {
    console.error('Error checking promotional notice status:', error);
  }
};

const closeModal = () => {
  showJoinCampaignModal.value = false;
};

watch(activeTab, async () => {
  currentPage.value = 0;
  await fetchCampaigns();
  // joined tab'ına geçince, eğer sessionStorage'dan gelen yeni kampanya(lar) varsa ekle
  if (
    activeTab.value === CAMPAIGN_TABS.joined &&
    !hasProcessedNewlyJoined.value &&
    newlyJoinedCampaigns.value.length > 0
  ) {
    await addNewlyJoinedCampaign();
  }
});

// Sayfa yüklendiğinde her iki tab için de toplam sayıları al
onMounted(async () => {
  // Seller info API'den categories ve brands'i al
  try {
    const sellerInfo = await getSellerInfo();
    categories.value = sellerInfo.categories || [];
    brands.value = sellerInfo.brands || [];
  } catch (error) {
    console.error('Error fetching seller info:', error);
  }

  fireTabEvents(activeTab.value);

  // SessionStorage'dan kampanyadan ayrılma toaster mesajını kontrol et
  const leaveCampaignToast = sessionStorage.getItem('leaveCampaignToast');
  if (leaveCampaignToast) {
    try {
      const toastData = JSON.parse(leaveCampaignToast);
      store.commit('common/SET_TOASTER_DATA', toastData);
      sessionStorage.removeItem('leaveCampaignToast');
    } catch (error) {
      console.error(
        'Error parsing leave campaign toast from sessionStorage:',
        error,
      );
    }
  }

  // SessionStorage'dan yeni eklenen kampanya bilgisini al
  // Her zaman array formatına çevir (tek obje gelirse array'e çevir)
  const storedCampaign = sessionStorage.getItem('newlyJoinedCampaign');
  if (storedCampaign) {
    try {
      const parsed = JSON.parse(storedCampaign);
      // Tek obje gelirse array'e çevir, zaten array ise direkt kullan
      newlyJoinedCampaigns.value = Array.isArray(parsed) ? parsed : [parsed];
      hasProcessedNewlyJoined.value = false;
    } catch (error) {
      console.error(
        'Error parsing newly joined campaign from sessionStorage:',
        error,
      );
      newlyJoinedCampaigns.value = [];
    }
  }

  // Önce mevcut tab'ı yükle
  await fetchCampaigns();

  // Yeni eklenen kampanyaları ekle (sadece joined tab'ında ve bir kez)
  if (
    activeTab.value === CAMPAIGN_TABS.joined &&
    newlyJoinedCampaigns.value.length > 0 &&
    !hasProcessedNewlyJoined.value
  ) {
    await addNewlyJoinedCampaign();
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.page-container {
  display: flex;
  width: 100%;
  flex-direction: column;

  .content-banner {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    margin-top: 16px;
    img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }
  }

  .title-header {
    padding: 16px 0;

    .title {
      color: $colorN80;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.2px;
      line-height: 26px;
    }
  }

  .content-wrapper {
    background: $colorN00;
    border-radius: 8px;
    border-bottom: 0.5px solid $colorN30;
    display: flex;
    flex-direction: column;

    .tabs-container {
      display: flex;
      gap: 12px;
      padding: 16px 24px;
      border-bottom: 0.5px solid $colorN30;

      .tab-button {
        flex: 1;
        min-height: 64px;
        padding: 12px 24px;
        border: 0.5px solid $colorD30;
        border-radius: 6px;
        background: $colorN00;
        color: $colorN80;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;

        .label {
          color: $colorN80;
          text-align: center;
          font-size: 14px;
          font-weight: 700;
          line-height: 18px;
          letter-spacing: -0.05px;
        }

        .count {
          color: $colorN60;
          text-align: center;
          font-size: 14px;
          font-weight: 700;
          line-height: 18px;
          letter-spacing: -0.05px;
        }

        &.active {
          background: $colorM05;
          border: 2px solid $colorM50;
          color: $colorN80;
          font-weight: 600;

          .label {
            color: $colorN80;
          }

          .count {
            color: $colorN80;
          }
        }
      }
    }
  }
}

.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .modal-content {
    position: relative;
    background: $colorN00;
    border-radius: 8px;
    padding: 24px;
    max-width: 480px;
    width: 90%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

    .modal-close {
      position: absolute;
      top: 16px;
      right: 16px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.7;
      }
    }

    :deep(.icon.lg) svg {
      width: 60px;
      height: 60px;
      margin-top: 16px;
    }

    .modal-description {
      color: $colorN80;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: -0.05px;
      text-align: center;
      margin: 0;
      font-weight: 700;
      line-height: 26px;
      letter-spacing: -0.2px;
    }

    :deep(.btn) {
      width: 100%;
    }
  }
}
</style>
